import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowLeft,
  MapPin,
  Phone,
  Clock,
  Send,
  Loader2,
  Check,
  MessageCircle,
  Navigation
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Social media icons
const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const PharmacyContactPage = ({ onNavigate }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const logoUrl = 'https://customer-assets.emergentagent.com/job_skincare-check-1/artifacts/6cemh04i_LOGO%20DEF.jpg';

  const pharmacyInfo = {
    name: 'Pharmacie Saint Ange Des Amis',
    tagline: 'Le Compagnon de votre Santé',
    address: 'Angre 7e tranche carrefour Bluetooth, Abidjan, Côte d\'Ivoire',
    phone: '07 78 41 52 77',
    gpsCode: '92V7+H8',
    mapsUrl: 'https://www.google.com/maps/place/92V7%2BH8+Abidjan,+C%C3%B4te+d\'Ivoire',
    hours: {
      weekdays: { label: 'Lundi - Vendredi', time: '7h30 - 20h00' },
      saturday: { label: 'Samedi', time: '7h30 - 13h00' },
      sunday: { label: 'Dimanche', time: 'Fermé' }
    },
    social: {
      facebook: 'https://www.facebook.com/profile.php?id=61558370265586',
      instagram: null, // Pas encore créé
      tiktok: 'https://www.tiktok.com/@pharmacie.saint.a0',
      linkedin: 'https://www.linkedin.com/company/pharmacie-saint-ange-des-amis'
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/pharmacy/messages`, {
        customer_name: formData.name,
        customer_phone: formData.phone,
        customer_email: formData.email,
        subject: formData.subject,
        message: formData.message
      });

      setSubmitted(true);
      toast.success('Message envoyé avec succès');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF8F3] to-white">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-[#E8DDD4] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onNavigate('home')}
              className="text-[#8B7355]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="h-8 w-auto object-contain cursor-pointer"
              onClick={() => onNavigate('home')}
            />
            <h1 className="font-bold text-[#5D4E37] text-lg">Contact</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[#5D4E37] mb-2">
                Nous contacter
              </h2>
              <p className="text-[#8B7355]">
                Notre équipe est à votre disposition pour répondre à toutes vos questions.
              </p>
            </div>

            <Card className="bg-white border-[#E8DDD4] shadow-lg">
              <CardContent className="p-6 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#C45C26]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#C45C26]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#5D4E37] mb-1">Adresse</h3>
                    <p className="text-[#8B7355]">{pharmacyInfo.address}</p>
                    <a 
                      href={pharmacyInfo.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[#C45C26] text-sm hover:underline mt-1"
                    >
                      <Navigation className="w-3 h-3" />
                      Voir l'itinéraire (GPS: {pharmacyInfo.gpsCode})
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#3B82F6]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#3B82F6]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#5D4E37] mb-1">Téléphone</h3>
                    <a 
                      href={`tel:${pharmacyInfo.phone.replace(/\s/g, '')}`}
                      className="text-[#5D4E37] hover:text-[#C45C26] font-medium"
                    >
                      {pharmacyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#2E7D32]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#2E7D32]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#5D4E37] mb-2">Horaires d'ouverture</h3>
                    <div className="space-y-1 text-sm">
                      {Object.entries(pharmacyInfo.hours).map(([key, { label, time }]) => (
                        <div key={key} className="flex justify-between gap-4">
                          <span className="text-[#8B7355]">{label}</span>
                          <span className={`font-medium ${time === 'Fermé' ? 'text-red-500' : 'text-[#5D4E37]'}`}>
                            {time}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Social Media */}
            <Card className="bg-white border-[#E8DDD4] shadow-lg">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-[#5D4E37]">Suivez-nous</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <a 
                    href={pharmacyInfo.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-[#1877F2] text-white rounded-xl flex items-center justify-center hover:opacity-90 transition-opacity"
                    aria-label="Facebook"
                  >
                    <FacebookIcon />
                  </a>
                  {pharmacyInfo.social.instagram && (
                    <a 
                      href={pharmacyInfo.social.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white rounded-xl flex items-center justify-center hover:opacity-90 transition-opacity"
                      aria-label="Instagram"
                    >
                      <InstagramIcon />
                    </a>
                  )}
                  <a 
                    href={pharmacyInfo.social.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-black text-white rounded-xl flex items-center justify-center hover:opacity-90 transition-opacity"
                    aria-label="TikTok"
                  >
                    <TikTokIcon />
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Quick Call CTA */}
            <Card className="bg-gradient-to-r from-[#C45C26] to-[#A84B1F] border-0 text-white">
              <CardContent className="p-6 text-center">
                <Phone className="w-10 h-10 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2">Besoin d'aide urgente ?</h3>
                <p className="text-white/90 mb-4 text-sm">
                  Appelez-nous directement pour une assistance rapide
                </p>
                <Button
                  variant="secondary"
                  className="bg-white text-[#C45C26] hover:bg-[#FDF8F3]"
                  asChild
                >
                  <a href={`tel:${pharmacyInfo.phone.replace(/\s/g, '')}`}>
                    <Phone className="w-4 h-4 mr-2" />
                    Appeler maintenant
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="bg-white border-[#E8DDD4] shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#5D4E37]">
                  <MessageCircle className="w-5 h-5 text-[#C45C26]" />
                  Envoyer un message
                </CardTitle>
              </CardHeader>
              <CardContent>
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-[#2E7D32]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-[#2E7D32]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#5D4E37] mb-2">
                      Message envoyé !
                    </h3>
                    <p className="text-[#8B7355] mb-6">
                      Nous vous répondrons dans les plus brefs délais.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ name: '', phone: '', email: '', subject: '', message: '' });
                      }}
                      className="border-[#E8DDD4] text-[#C45C26]"
                    >
                      Envoyer un autre message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="text-[#5D4E37]">Nom complet *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Votre nom"
                        className="mt-1 border-[#E8DDD4] focus:border-[#C45C26]"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-[#5D4E37]">Téléphone *</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="07 XX XX XX XX"
                        className="mt-1 border-[#E8DDD4] focus:border-[#C45C26]"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-[#5D4E37]">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="votre@email.com"
                        className="mt-1 border-[#E8DDD4] focus:border-[#C45C26]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-[#5D4E37]">Sujet</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="Objet de votre message"
                        className="mt-1 border-[#E8DDD4] focus:border-[#C45C26]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-[#5D4E37]">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Votre message..."
                        className="mt-1 border-[#E8DDD4] focus:border-[#C45C26]"
                        rows={4}
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#C45C26] hover:bg-[#A84B1F]"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Envoyer le message
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#5D4E37] text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <img 
            src={logoUrl} 
            alt="Pharmacie Saint Ange Des Amis" 
            className="h-16 w-auto object-contain bg-white rounded-lg p-2 mx-auto mb-4"
          />
          <p className="text-[#D4C4B0] text-sm">
            {pharmacyInfo.tagline}
          </p>
          <p className="text-[#D4C4B0] text-xs mt-4">
            &copy; 2026 Pharmacie Saint Ange Des Amis. Tous droits réservés.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PharmacyContactPage;
